import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import Box from '@amzn/awsui-components-console/box';
import Spinner from '@amzn/awsui-components-console/spinner';
import { useListNamespacesLazyQuery } from 'src/generated/graphql';
import LandingPage from './LandingPage';
import { SEARCH_PATH } from 'src/common/routes';

/** Probe page size — the maximum the Smithy pagination parameters allow. */
export const PROBE_PAGE_SIZE = 100;

/**
 * Caps the walk so a caller whose permission cache is dominated by expired or
 * non-VIEW rows cannot stall the route with an unbounded token walk.
 */
export const MAX_PROBE_PAGES = 50;

/**
 * Entry gate for the "/" route. On load it asks whether the caller can see any
 * namespace at all: if so it sends them to search, the primary discovery
 * surface. Otherwise — no accessible namespaces, or the request failed — it
 * renders the landing page, so a user with nothing to search is never dropped on
 * an empty results view and a probe failure never leaves a blank screen.
 *
 * Only the existence of an accessible namespace matters, never which one, so the
 * probe stops at the first non-empty page. It still has to walk `nextToken` past
 * empty pages, because `listNamespaces` pages the caller's own permission-cache
 * rows and then drops entries from each page it has already read. Expiry is the
 * filter that makes this bite: expired rows are excluded by a DynamoDB filter
 * expression, which runs after the page is read, and TTL deletion is lazy enough
 * (~48h) that stale rows are normal — so a user who once held many namespaces
 * and now holds one can have their whole first page consumed by them. The
 * VIEW-action filter runs after the page is mapped and carries the same hazard.
 * Either can yield an empty or short page alongside a non-null `nextToken`, so
 * capping the probe at one page would strand a permitted user on the landing
 * page.
 *
 * `no-cache` because the probe reads each page once and never re-reads it, so
 * retaining up to `MAX_PROBE_PAGES` pages of namespaces would be pure memory
 * cost.
 */
const LandingEntry: React.FC = () => {
  const [executeListNamespaces] = useListNamespacesLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [hasAccessibleNamespace, setHasAccessibleNamespace] = useState(false);
  const [probing, setProbing] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const probeForAccessibleNamespace = async () => {
      let nextToken: string | undefined = undefined;

      for (let page = 0; page < MAX_PROBE_PAGES; page++) {
        const { data, error } = await executeListNamespaces({
          variables: { pageSize: PROBE_PAGE_SIZE, nextToken },
        });
        if (cancelled) return;

        // A failed page ends the probe; the landing page is the fallback.
        if (error || !data) return;

        if (data.listNamespaces.namespaces.length > 0) {
          setHasAccessibleNamespace(true);
          return;
        }

        nextToken = data.listNamespaces.nextToken ?? undefined;
        if (!nextToken) return;
      }
    };

    probeForAccessibleNamespace()
      .catch(() => {
        // Same fallback as a failed page — render the landing page.
      })
      .finally(() => {
        if (!cancelled) setProbing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [executeListNamespaces]);

  if (hasAccessibleNamespace) {
    return <Navigate to={SEARCH_PATH} replace />;
  }

  if (probing) {
    return (
      <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
        <Spinner size="large" />
      </Box>
    );
  }

  return <LandingPage />;
};

export default LandingEntry;
